import { Component, OnInit, ViewChild } from '@angular/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { isNotEmpty } from 'src/app/extensions/utilities';
import { SIGNAL_ITEM_KEY, SPECIAL_OFFER_STATUS } from 'src/app/extensions/constants/commonConstants';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { forkJoin } from 'rxjs';
import { NgForm } from '@angular/forms';
import { SPECIAL_OFFER_ROUTES } from 'src/app/core/constants/routes-constants';

@Component({
  selector: 'app-special-offers',
  templateUrl: './campaign-details.component.html',
  styleUrls: ['./campaign-details.component.scss'],
  standalone: false,
})
export class CampaignDetailsComponent implements OnInit {
  @ViewChild('form') public form: NgForm;
  private stateSignalItem: any;
  canUnloadByPassForm: boolean = false;

  public specialOffer: app.models.GetSpecialOfferModel = {
    title: '',
    campaignDocuments: [],
  } as app.models.GetSpecialOfferModel;
  specialOfferId: string;

  constructor(
    private readonly _globalService: GlobalService,
    private readonly _router: Router,
    private readonly dictionaryAPIService: DictionaryAPIService,
    private readonly partnerAPIService: PartnerAPIService,
    private _activatedRoute: ActivatedRoute
  ) {}

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';
  public today: Date = new Date();
  public campaignTypes: app.models.DictionaryDataModel[];
  public currencies: app.models.DictionaryDataModel[];
  public dealers: app.models.PartnerCompany[];
  public manufacturers: app.models.PartnerCompany[];
  public specialOfferStatus = SPECIAL_OFFER_STATUS;

  ngOnInit(): void {
    this.specialOfferId = this._activatedRoute.snapshot.params['specialOfferId'];
    this.stateSignalItem = this._globalService.getSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA);
    if (isNotEmpty(this.stateSignalItem)) {
      this.specialOffer = this.stateSignalItem.data as app.models.GetSpecialOfferModel;
      this.specialOffer.dictionaryCampaignTypeId = this.specialOffer.dictionaryCampaignTypeId.toUpperCase();
    } else {
      this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
      this.stateSignalItem = { data: { isNewCamapign: true, specialOfferId: '' } };
      if (this.specialOfferId) {
        this._router.navigate([
          SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
          SPECIAL_OFFER_ROUTES.PREVIEW,
          this.specialOfferId,
        ]);
      }
    }
    this.loadData();
  }

  loadData() {
    let source = forkJoin([
      this.dictionaryAPIService.getDictionaryCampaignTypes(),
      this.partnerAPIService.getDealers(),
      this.partnerAPIService.getManufacturers(),
      this.dictionaryAPIService.getDictionaryCurrencies(),
    ]);
    source.subscribe(([dictionaryCampaignTypes, dealers, manufacturers, dictionaryCurrencies]) => {
      this.campaignTypes = dictionaryCampaignTypes;
      this.dealers = dealers;
      this.manufacturers = manufacturers;
      this.currencies = dictionaryCurrencies;
    });
  }

  disabledPastDates = (date: Date): boolean => {
    const yesterday = (d => new Date(d.setDate(d.getDate() - 1)))(new Date());
    return date.getTime() < yesterday.getTime();
  };

  disabledDatesBeforeStartDate = (date: Date): boolean => {
    if (this.specialOffer.startDate) {
      const startDatePlusOneDay = new Date(this.specialOffer.startDate);
      startDatePlusOneDay.setDate(startDatePlusOneDay.getDate() + 2);
      return date < startDatePlusOneDay;
    } else {
      const startDatePlusOneDay = new Date(this.today);
      startDatePlusOneDay.setDate(startDatePlusOneDay.getDate() + 2);
      return date < startDatePlusOneDay;
    }
  };

  redirectToSpecialOffersList() {
    this._router.navigate([SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS]);
  }

  nextStepHandler() {
    this.canUnloadByPassForm = true;
    this.stateSignalItem = {
      data: { ...this.stateSignalItem.data, ...this.specialOffer, isCampaignDetailsFormDirty: this.form.dirty },
    };
    this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, this.stateSignalItem);
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.FINANCING_DETAILS,
      this.specialOfferId ?? '',
    ]);
  }

  campaignTypeSelectedHandler(event) {
    this.specialOffer.campaignType = this.campaignTypes.find(x => x.id === event)?.value;
  }

  dealerSelectHandler(event) {
    this.specialOffer.dealerName = this.dealers.find(x => x.partnerCompanyId === event)?.name;
  }

  manufacturerSelectHandler(event) {
    this.specialOffer.manufacturerName = this.manufacturers.find(x => x.partnerCompanyId === event)?.name;
  }

  currencySelectHandler(event) {
    this.specialOffer.currencyCode = this.currencies.find(x => x.id === event)?.code;
  }
}
